import React, { createContext, useContext, useReducer, useRef, useCallback } from 'react';

export interface VideoItem {
  id: string;
  title: string;
  artist: string;
  thumbnailUrl: string;
  videoUrl: string;
  trailerUrl?: string;
  duration: number;
  price: number;
  isPurchased: boolean;
}

interface VideoState {
  currentVideo: VideoItem | null;
  isPlaying: boolean;
  isMiniPlayer: boolean;
  isFullscreen: boolean;
  volume: number;
  currentTime: number;
  duration: number;
  playbackMode: 'trailer' | 'preview' | 'full';
  previewTimeRemaining: number;
  trailerTimeRemaining: number;
  showPurchasePrompt: boolean;
  hasUnlimited: boolean;
}

type VideoAction =
  | { type: 'SET_VIDEO'; payload: VideoItem }
  | { type: 'PLAY' }
  | { type: 'PAUSE' }
  | { type: 'TOGGLE_PLAY' }
  | { type: 'SET_VOLUME'; payload: number }
  | { type: 'SET_CURRENT_TIME'; payload: number }
  | { type: 'SET_DURATION'; payload: number }
  | { type: 'SET_MINI_PLAYER'; payload: boolean }
  | { type: 'SET_FULLSCREEN'; payload: boolean }
  | { type: 'SET_PLAYBACK_MODE'; payload: 'trailer' | 'preview' | 'full' }
  | { type: 'SET_PREVIEW_TIME_REMAINING'; payload: number }
  | { type: 'SET_TRAILER_TIME_REMAINING'; payload: number }
  | { type: 'SHOW_PURCHASE_PROMPT'; payload: boolean }
  | { type: 'SET_HAS_UNLIMITED'; payload: boolean }
  | { type: 'CLOSE_VIDEO' }
  | { type: 'SKIP_TRAILER' }
  | { type: 'VIDEO_PURCHASED' };

const initialState: VideoState = {
  currentVideo: null,
  isPlaying: false,
  isMiniPlayer: false,
  isFullscreen: false,
  volume: 1,
  currentTime: 0,
  duration: 0,
  playbackMode: 'full',
  previewTimeRemaining: 30,
  trailerTimeRemaining: 0,
  showPurchasePrompt: false,
  hasUnlimited: false,
};

const videoReducer = (state: VideoState, action: VideoAction): VideoState => {
  switch (action.type) {
    case 'SET_VIDEO':
      return {
        ...state,
        currentVideo: action.payload,
        isPlaying: true,
        currentTime: 0,
        showPurchasePrompt: false,
      };
    case 'PLAY':
      return { ...state, isPlaying: true };
    case 'PAUSE':
      return { ...state, isPlaying: false };
    case 'TOGGLE_PLAY':
      return { ...state, isPlaying: !state.isPlaying };
    case 'SET_VOLUME':
      return { ...state, volume: action.payload };
    case 'SET_CURRENT_TIME':
      return { ...state, currentTime: action.payload };
    case 'SET_DURATION':
      return { ...state, duration: action.payload };
    case 'SET_MINI_PLAYER':
      return { ...state, isMiniPlayer: action.payload, isFullscreen: false };
    case 'SET_FULLSCREEN':
      return { ...state, isFullscreen: action.payload, isMiniPlayer: false };
    case 'SET_PLAYBACK_MODE':
      return { ...state, playbackMode: action.payload };
    case 'SET_PREVIEW_TIME_REMAINING':
      return { ...state, previewTimeRemaining: action.payload };
    case 'SET_TRAILER_TIME_REMAINING':
      return { ...state, trailerTimeRemaining: action.payload };
    case 'SHOW_PURCHASE_PROMPT':
      return { ...state, showPurchasePrompt: action.payload, isPlaying: !action.payload };
    case 'SET_HAS_UNLIMITED':
      return { ...state, hasUnlimited: action.payload };
    case 'CLOSE_VIDEO':
      return { ...initialState, volume: state.volume, hasUnlimited: state.hasUnlimited };
    case 'SKIP_TRAILER':
      if (state.hasUnlimited || state.currentVideo?.isPurchased) {
        return { ...state, playbackMode: 'full', showPurchasePrompt: false };
      }
      return { ...state, showPurchasePrompt: true, isPlaying: false };
    case 'VIDEO_PURCHASED':
      return {
        ...state,
        currentVideo: state.currentVideo ? { ...state.currentVideo, isPurchased: true } : null,
        playbackMode: 'full',
        showPurchasePrompt: false,
        isPlaying: true,
      };
    default:
      return state;
  }
};

interface VideoContextType {
  state: VideoState;
  dispatch: React.Dispatch<VideoAction>;
  videoRef: React.RefObject<HTMLVideoElement>;
  playVideo: (video: VideoItem, hasUnlimited: boolean) => void;
  toggleMiniPlayer: () => void;
  closeVideo: () => void;
}

const VideoContext = createContext<VideoContextType | null>(null);

export const VideoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(videoReducer, initialState);
  const videoRef = useRef<HTMLVideoElement>(null);

  const playVideo = useCallback((video: VideoItem, hasUnlimited: boolean) => {
    dispatch({ type: 'SET_HAS_UNLIMITED', payload: hasUnlimited });

    let playbackMode: 'trailer' | 'preview' | 'full' = 'full';

    if (hasUnlimited || video.isPurchased) {
      playbackMode = 'full';
    } else if (video.trailerUrl) {
      playbackMode = 'trailer';
    } else {
      playbackMode = 'preview';
      dispatch({ type: 'SET_PREVIEW_TIME_REMAINING', payload: 30 });
    }

    dispatch({ type: 'SET_PLAYBACK_MODE', payload: playbackMode });
    dispatch({ type: 'SET_VIDEO', payload: video });
  }, []);

  const toggleMiniPlayer = useCallback(() => {
    dispatch({ type: 'SET_MINI_PLAYER', payload: !state.isMiniPlayer });
  }, [state.isMiniPlayer]);

  const closeVideo = useCallback(() => {
    dispatch({ type: 'CLOSE_VIDEO' });
  }, []);

  return (
    <VideoContext.Provider value={{ state, dispatch, videoRef, playVideo, toggleMiniPlayer, closeVideo }}>
      {children}
    </VideoContext.Provider>
  );
};

export const useVideo = () => {
  const context = useContext(VideoContext);
  if (!context) {
    throw new Error('useVideo must be used within a VideoProvider');
  }
  return context;
};
